<script setup lang="ts">
import SelectComponent from '@/components/select/SelectComponent.vue'
import LeafletAddressPicker from '@/components/address/LeafletAddressPicker.vue'
import {
    dataModifyAddressHappy,
    dataModifyAddressHappyEmpty
} from '@/happyTheme/event/dashboard/address/dataModifyAddressHappy'
import {
    showModalAddressHappy,
    showModalAddressHappyTransition
} from '@/happyTheme/event/dashboard/address/showModalAddressHappy'
import type { IDataModifyAddressHappy } from '@/happyTheme/interfaces/dashboard/address/IDataModifyAddressHappy'
import { AddressService } from '@/services/AddressService'
import { configDeliveryZones } from '@/storage/setting/configDeliveryZones'
import type { DeliveryZone } from '@/types/stores/setting/DeliveryZoneInterface'
import { computed, onMounted, ref } from 'vue'

const errorToggle = ref(false)
const isSend = ref(false)
const company = ref(
    dataModifyAddressHappy.value.company ? dataModifyAddressHappy.value.company : ''
)
const city = ref(
    dataModifyAddressHappy.value
        ? dataModifyAddressHappy.value
        : dataModifyAddressHappyEmpty
)
const zone = ref(
    dataModifyAddressHappy.value.zone_id
        ? dataModifyAddressHappy.value
        : dataModifyAddressHappyEmpty
)

const address1 = ref(
    dataModifyAddressHappy.value.address_1 ? dataModifyAddressHappy.value.address_1 : ''
)
const address2 = ref(
    dataModifyAddressHappy.value.address_2 ? dataModifyAddressHappy.value.address_2 : ''
)
const defaultAddress = ref(
    dataModifyAddressHappy.value.default ? dataModifyAddressHappy.value.default : false
)

// refactored — harita üzerinden seçilen konum (lat/lng + display_name). Backend
// kolonları hazır olunca addAddress payload'ına dahil edilecek.
interface PickedLocation {
    lat: number
    lng: number
    display_name: string
    address?: Record<string, string>
}

// refactored — edit modunda backend custom_field JSON'undan geo'yu parse et; ilk mount'ta
// map picker initialCenter bu noktaya konuşlandırılır + marker otomatik yerleştirilir.
function parseInitialPickedLocation(): PickedLocation | null {
    const raw = dataModifyAddressHappy.value?.custom_field
    if (!raw) return null
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        const geo = parsed?.geo
        if (
            geo &&
            typeof geo.lat === 'number' &&
            typeof geo.lng === 'number'
        ) {
            return {
                lat: geo.lat,
                lng: geo.lng,
                display_name: geo.display_name ?? ''
            }
        }
    } catch {
        // Bozuk JSON -> sessiz geç
    }
    return null
}

const pickedLocation = ref<PickedLocation | null>(parseInitialPickedLocation())

function handleLocationPicked(v: PickedLocation | null) {
    pickedLocation.value = v
    // İlk seçimde address_1 boşsa otomatik doldur (kullanıcı sonra düzenleyebilir)
    if (v && (!address1.value || address1.value.trim() === '')) {
        address1.value = v.display_name?.split(',').slice(0, 2).join(',').trim() || ''
    }
}

const errorEmptyForm = ref({
    city: false,
    zone: false,
    address1: false
})

const zones = computed(() => {
    const zones = configDeliveryZones.value.reduce<DeliveryZone[]>((acc, zone) => {
        if (!acc.some((z) => z.zone_id === zone.zone_id)) {
            acc.push(zone)
        }
        return acc
    }, [])
    return zones
})

const citys = computed(() => {
    if (!zone.value.zone_id) return []

    return configDeliveryZones.value
        .filter((z) => z.zone_id === zone.value.zone_id)
        .map((zone) => zone)
})

function toggleShowModalAddressHappy() {
    showModalAddressHappyTransition.value = !showModalAddressHappyTransition.value
    setTimeout(() => {
        showModalAddressHappy.value = !showModalAddressHappy.value
        dataModifyAddressHappy.value = dataModifyAddressHappyEmpty
    }, 300)
}

function selectRegion(selectedObject: IDataModifyAddressHappy) {
    zone.value = selectedObject
    city.value = dataModifyAddressHappyEmpty
}

function selectedCity(selectedObject: IDataModifyAddressHappy) {
    city.value = selectedObject ? selectedObject : dataModifyAddressHappyEmpty
}

function sendError(error: boolean) {
    errorToggle.value = error
    setTimeout(() => {
        errorToggle.value = false
    }, 500)
}

async function addAddress() {
    if (!city.value.city) {
        errorEmptyForm.value.city = true
    }
    if (!zone.value.zone_id) {
        errorEmptyForm.value.zone = true
    }
    if (address1.value.length < 1) {
        errorEmptyForm.value.address1 = true
    }
    if (
        errorEmptyForm.value.city ||
        errorEmptyForm.value.zone ||
        errorEmptyForm.value.address1
    ) {
        setTimeout(() => {
            errorEmptyForm.value.city = false
            errorEmptyForm.value.zone = false
            errorEmptyForm.value.address1 = false
        }, 2000)
        return
    }

    const data = {
        company: company.value,
        city: city.value,
        zone: zone.value,
        address: address1.value,
        address_2: address2.value,
        default: defaultAddress.value,
        // refactored — map picker'dan gelen tam konum (backend kolonu eklenene kadar
        // yok sayılır / opsiyonel olarak iletilir).
        latitude: pickedLocation.value?.lat ?? null,
        longitude: pickedLocation.value?.lng ?? null,
        geo_display_name: pickedLocation.value?.display_name ?? null
    }

    const addressService = new AddressService()
    const response = await addressService.addAddress(data)

    if (response.class === 'success') {
        isSend.value = true
        toggleShowModalAddressHappy()
    }
}

async function updateAddress() {
    if (!city.value.city) {
        errorEmptyForm.value.city = true
    }
    if (!zone.value.zone_id) {
        errorEmptyForm.value.zone = true
    }
    if (address1.value.length < 1) {
        errorEmptyForm.value.address1 = true
    }
    if (
        errorEmptyForm.value.city ||
        errorEmptyForm.value.zone ||
        errorEmptyForm.value.address1
    ) {
        setTimeout(() => {
            errorEmptyForm.value.city = false
            errorEmptyForm.value.zone = false
            errorEmptyForm.value.address1 = false
        }, 2000)
        return
    }

    const data = {
        address_id: dataModifyAddressHappy.value.address_id,
        company: company.value,
        city: city.value,
        zone: zone.value,
        address_1: address1.value,
        address_2: address2.value,
        default: defaultAddress.value,
        // refactored — map picker'dan gelen tam konum.
        latitude: pickedLocation.value?.lat ?? null,
        longitude: pickedLocation.value?.lng ?? null,
        geo_display_name: pickedLocation.value?.display_name ?? null
    }

    const addressService = new AddressService()
    const response = await addressService.updateAddress(data)

    if (response.class === 'success') {
        isSend.value = true
        toggleShowModalAddressHappy()
    }
}

onMounted(() => {
    isSend.value = false
})
</script>

<template>
    <section class="section-modal-address-happy">
        <section
            class="section-overlay-modal-address-happy"
            @click="toggleShowModalAddressHappy()"
        ></section>

        <transition
            enter-active-class="transition-all duration-300 ease-in-out"
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-300 ease-in-out"
            leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <section
                class="section-parent-modal-address-happy"
                v-if="showModalAddressHappyTransition"
            >
                <section class="section-content-modal-address-happy">
                    <div class="div-header-modal-address-happy">
                        <h3 v-if="dataModifyAddressHappy.address_id">
                            {{ $t('editAddress') }}
                        </h3>
                        <h3 v-else>{{ $t('addAddress') }}</h3>
                        <p class="text-required-address-happy">*{{ $t('mandatory') }}</p>
                    </div>

                    <section class="section-form-modal-address-happy">
                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <label for="postalCode" class="label-address-happy">
                                    {{ $t('addressFormZoneLabel') }}<span class="span-required-address-happy"
                                        >*
                                    </span>
                                </label>
                            </div>

                            <SelectComponent
                                :options="zones"
                                value-key="zone"
                                :placeholder="$t('addressFormSelectZone')"
                                required
                                :selected="zone"
                                @change="selectRegion"
                                :error="errorToggle || errorEmptyForm.zone"
                                theme="happy"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <label for="name_city" class="label-address-happy"
                                    >{{ $t('addressFormCityLabel') }}<span class="span-required-address-happy"
                                        >*
                                    </span>
                                </label>
                            </div>
                            <SelectComponent
                                :options="citys"
                                display-key="city"
                                :placeholder="$t('addressFormSelectCity')"
                                :selected="city"
                                :disabled="!zone.zone_id"
                                @change="selectedCity"
                                @send-error="sendError"
                                :error="errorEmptyForm.city"
                                theme="happy"
                            />
                        </div>

                        <!-- refactored — Leaflet/OSM map picker: kullanıcı adres arar veya
                             haritadan nokta seçer; lat/lng + display_name alınır. -->
                        <div class="div-group-map-picker-modal-address-happy">
                            <LeafletAddressPicker
                                :model-value="pickedLocation"
                                @update:model-value="handleLocationPicked"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy-company">
                            <div class="div-label-required-modal-address-happy">
                                <label for="company" class="label-address-happy">
                                    {{ $t('company') }}
                                </label>
                            </div>
                            <input
                                class="input-address-happy"
                                type="text"
                                id="company"
                                v-model="company"
                                :placeholder="$t('company')"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <label for="address_1" class="label-address-happy"
                                    >{{ $t('addressFormStreetLabel') }}<span class="span-required-address-happy"
                                        >*
                                    </span>
                                </label>
                            </div>
                            <input
                                :class="{ shake: errorEmptyForm.address1 }"
                                class="input-address-happy"
                                type="text"
                                id="address_1"
                                v-model="address1"
                                :placeholder="$t('addressFormStreetLabel')"
                            />
                        </div>

                        <div class="div-group-textarea-modal-address-happy">
                            <label for="address_2" class="pl-4">
                                {{ $t('addressComplement') }}
                                <span class="text-sm">({{ $t('optional') }})</span>
                            </label>
                            <textarea
                                class="textarea-address-happy"
                                id="address_2"
                                v-model="address2"
                            ></textarea>
                        </div>

                        <div class="div-group-input-default-modal-address-happy">
                            <p class="label-address-default-happy">
                                {{ $t('defaultAddressQuestion') }} :
                            </p>
                            <div class="div-radio-button-modal-address-happy">
                                <label for="default_address">{{ $t('yes') }}</label>
                                <input
                                    type="radio"
                                    id="default_address"
                                    v-model="defaultAddress"
                                    value="true"
                                />
                            </div>
                            <div class="div-radio-button-modal-address-happy">
                                <label for="default_address_no">{{ $t('no') }}</label>
                                <input
                                    type="radio"
                                    id="default_address_no"
                                    v-model="defaultAddress"
                                    value="false"
                                    checked
                                />
                            </div>
                        </div>
                    </section>

                    <div class="div-button-modal-address-happy">
                        <button
                            class="button-close-modal-address-happy"
                            @click="toggleShowModalAddressHappy()"
                        >
                            {{ $t('close') }}
                        </button>
                        <button
                            v-if="!dataModifyAddressHappy.address_id && isSend === false"
                            class="button-submit-modal-address-happy"
                            @click="addAddress()"
                        >
                            {{ $t('save') }}
                        </button>
                        <button
                            v-else-if="
                                dataModifyAddressHappy.address_id && isSend === false
                            "
                            class="button-submit-modal-address-happy"
                            @click="updateAddress()"
                        >
                            {{ $t('edit') }}
                        </button>
                    </div>
                </section>
            </section>
        </transition>
    </section>
</template>

<style scoped>
.section-modal-address-happy {
    @apply fixed flex justify-center top-0 left-0 w-full h-full z-30;
}
.section-overlay-modal-address-happy {
    /* refactored — bg-t-text dark temalarda near-white (ifo-dark #E6EDF3) olduğu
       için dim yerine krem wash üretiyordu. Siyah/30 ile tüm temalarda
       doğru karartma sağlıyoruz. */
    @apply w-full h-full bg-black/30;
}

.section-parent-modal-address-happy {
    @apply absolute z-40  mt-12 px-2 flex justify-center;
}
.section-content-modal-address-happy {
    /* refactored: multi-theme border/bg/shadow -> semantic tokens */
    @apply w-full border-x-2 border-b-2 gap-3 rounded-md shadow overflow-y-auto max-h-[90vh] bg-t-card text-t-text border-t-accent shadow-t-border;
    @apply happy-purple-gradient:bg-gradient-to-t happy-purple-gradient:to-bg-purple-dark happy-purple-gradient:from-bg-purple-light;
    /* ifo-dark: thin soft gold ring (all 4 sides, since tinted header won't seal top)
       + subtle inner gold glow, matching the dashboard card pattern */
    @apply happy-ifo-dark:border happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:shadow-[inset_0_0_60px_rgba(212,160,23,0.05),0_0_18px_rgba(212,160,23,0.08)];
}

.div-header-modal-address-happy {
    /* refactored: multi-theme bg/text/border -> semantic tokens */
    @apply relative flex justify-between p-4 rounded-t-sm font-semibold border-t bg-t-accent text-t-on-accent border-t-accent;
    /* ifo-dark: soft gold header band (tinted bg + solid accent text + thin accent
       underline + spaced uppercase), matches HistoryOrder/Address/Info card headers */
    @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:text-t-accent happy-ifo-dark:font-medium
           happy-ifo-dark:border-t-0 happy-ifo-dark:border-b happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:uppercase happy-ifo-dark:tracking-[0.2em] happy-ifo-dark:text-sm
           happy-ifo-dark:py-3 happy-ifo-dark:px-5;
}

.text-required-address-happy {
    /* refactored: multi-theme text -> text-t-accent */
    @apply absolute top-2 right-2 font-semibold text-xs text-t-accent;
}
.section-form-modal-address-happy {
    /* refactored: text -> text-t-text */
    @apply w-full flex flex-col gap-3 p-4 text-t-text;
}
.div-group-input-modal-address-happy-company {
    @apply flex flex-col gap-1 w-full;
}
.div-group-input-modal-address-happy {
    @apply flex flex-col gap-1;
}
.div-group-map-picker-modal-address-happy {
    @apply flex flex-col gap-1 w-full;
}
.div-group-input-default-modal-address-happy {
    @apply flex gap-2 w-full justify-end pt-2;
}

.div-group-textarea-modal-address-happy {
    @apply flex flex-col gap-1;
}

.label-address-default-happy {
    /* refactored: text -> text-t-text */
    @apply font-medium text-t-text;
}

.div-radio-button-modal-address-happy {
    @apply flex items-center gap-1;
}

/* refactored — ifo-dark: native radio (mavi) tema amber'ine boyandı. */
.div-radio-button-modal-address-happy input[type='radio'] {
    @apply happy-ifo-dark:accent-t-accent;
}

.div-label-required-modal-address-happy {
    @apply flex flex-row pl-4;
}

.span-required-address-happy {
    /* refactored: multi-theme text -> text-t-accent */
    @apply font-semibold text-t-accent;
}

.input-address-happy {
    /* refactored: border/text -> semantic tokens */
    @apply w-full min-h-8 py-1 rounded-md border-2 px-3 text-center text-t-text border-t-border;
    /* refactored — ifo-dark: dark-transparan bg + amber/30 border, pill-uyumlu. */
    @apply happy-ifo-dark:bg-t-accent/5
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:rounded-full
           happy-ifo-dark:py-1.5
           happy-ifo-dark:text-t-text
           happy-ifo-dark:placeholder:text-t-text-muted;
}

.textarea-address-happy {
    /* refactored: border/text -> semantic tokens. İçerik (Apt No, Kat vb.) çok
       satırlı olabildiği için sola hizalı yazılsın. */
    @apply w-full min-h-24 rounded-md border-2 px-3 py-1.5 text-left text-t-text border-t-border;
    /* refactored — ifo-dark: textarea için dark bg + amber border (rounded-lg). */
    @apply happy-ifo-dark:bg-t-accent/5
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:rounded-xl
           happy-ifo-dark:text-t-text
           happy-ifo-dark:placeholder:text-t-text-muted;
}

.input-address-happy:focus,
.textarea-address-happy:focus {
    /* refactored: multi-theme border -> border-t-accent */
    @apply outline-none border-t-accent;
    @apply happy-ifo-dark:border-t-accent happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:shadow-[0_0_8px_rgba(212,160,23,0.3)];
}

.option-zones-happy {
    @apply pr-4;
}

.div-button-modal-address-happy {
    /* refactored: text -> text-t-text */
    @apply w-full flex justify-end gap-2 p-3 text-t-text;
}

.button-close-modal-address-happy {
    /* refactored: multi-theme bg/text/shadow -> semantic tokens */
    @apply px-4 py-1 w-32 rounded-md font-semibold shadow-sm bg-t-bg text-t-text shadow-t-border;
    /* refactored — ifo-dark: yumuşak outline pill. */
    @apply happy-ifo-dark:bg-t-accent/5
           happy-ifo-dark:text-t-text-muted
           happy-ifo-dark:border
           happy-ifo-dark:border-t-border
           happy-ifo-dark:rounded-full
           happy-ifo-dark:shadow-none;
}

.button-close-modal-address-happy:active,
.button-close-modal-address-happy:focus {
    /* refactored: multi-theme active/focus -> bg-t-border */
    @apply bg-t-border;
    @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:border-t-accent/50 happy-ifo-dark:text-t-text;
}

.button-submit-modal-address-happy {
    /* refactored: multi-theme bg/text/shadow -> semantic tokens */
    @apply px-4 py-1 w-32 rounded-md font-semibold shadow-sm bg-t-accent text-t-on-accent shadow-t-border;
    /* refactored — ifo-dark: Seç/onayla pill. */
    @apply happy-ifo-dark:bg-t-accent/10
           happy-ifo-dark:!text-t-accent
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/40
           happy-ifo-dark:rounded-full
           happy-ifo-dark:shadow-none;
}

.button-submit-modal-address-happy:enabled:active,
.button-submit-modal-address-happy:enabled:focus {
    /* refactored: multi-theme active/focus -> bg-t-accent-active */
    @apply bg-t-accent-active shadow-sm;
    @apply happy-ifo-dark:bg-t-accent/20 happy-ifo-dark:border-t-accent/70;
}

.button-submit-modal-address-happy:disabled {
    /* refactored: bg -> bg-t-border */
    @apply bg-t-border;
}

@media screen and (min-width: 660px) {
    .section-content-modal-address-happy {
        @apply w-[40rem];
    }
    .section-form-modal-address-happy {
        @apply grid grid-cols-2;
    }
    .div-group-input-modal-address-happy-company {
        @apply mx-auto;
    }
    .div-group-input-default-modal-address-happy {
        @apply col-span-2;
    }
    .div-group-textarea-modal-address-happy {
        @apply col-span-2;
    }
    .div-group-map-picker-modal-address-happy {
        @apply col-span-2;
    }
}

@media (hover: hover) {
    .button-close-modal-address-happy:hover {
        /* refactored: multi-theme hover -> bg-t-border */
        @apply bg-t-border;
        @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:border-t-accent/50 happy-ifo-dark:text-t-text;
    }

    .button-submit-modal-address-happy:enabled:hover {
        /* refactored: multi-theme hover -> bg-t-accent-hover */
        @apply bg-t-accent-hover;
        @apply happy-ifo-dark:bg-t-accent/20 happy-ifo-dark:border-t-accent/70 happy-ifo-dark:shadow-[0_0_14px_rgba(212,160,23,0.25)];
    }
}

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(5deg);
    }
    30% {
        transform: rotate(0eg);
    }
    45% {
        transform: rotate(-5deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-5deg);
    }
    100% {
        transform: rotate(0deg);
    }
}
.shake {
    @apply border-header-red-happy;
    animation: shake 0.5s ease-in-out;
}
</style>
